import { MaritimeWarningSettingsEnabledDomainsEnum } from "@/lib/api/types";

/** OpenAPI `MaritimeArea.area_category` wire values (see generated maritime-area). */
const API_AREA_CATEGORY = {
  coastal: "coastal",
  northSea: "north_sea",
} as const;

/** Forecast domain public_ids for `GET /data/maritime/forecasts/{domain_id}`. */
export const MARITIME_FORECAST_DOMAINS = {
  coastal: "coastal",
  northSea: "north-sea",
  dutchContinentalShelf: "dutch-continental-shelf",
} as const;

export type MaritimeForecastDomainId =
  (typeof MARITIME_FORECAST_DOMAINS)[keyof typeof MARITIME_FORECAST_DOMAINS];

/** `MaritimeWarningSettings.enabled_domains` array values. */
const API_ENABLED_DOMAIN = {
  coastal: MaritimeWarningSettingsEnabledDomainsEnum.Kust,
  northSea: MaritimeWarningSettingsEnabledDomainsEnum.Noordzee,
} as const;

export const MARITIME_WARNING_DOMAINS = {
  coastal: {
    id: "coastal" as const,
    api: {
      areaCategory: API_AREA_CATEGORY.coastal,
      enabledDomain: API_ENABLED_DOMAIN.coastal,
    },
    forecastDomainId: MARITIME_FORECAST_DOMAINS.coastal,
    i18nKey: "coastal",
  },
  northSea: {
    id: "north-sea" as const,
    api: {
      areaCategory: API_AREA_CATEGORY.northSea,
      enabledDomain: API_ENABLED_DOMAIN.northSea,
    },
    forecastDomainId: MARITIME_FORECAST_DOMAINS.northSea,
    i18nKey: "north-sea",
  },
} as const;

export type MaritimeWarningDomainRegistryKey = keyof typeof MARITIME_WARNING_DOMAINS;

export type MaritimeWarningDomainId =
  (typeof MARITIME_WARNING_DOMAINS)[MaritimeWarningDomainRegistryKey]["id"];

export const MARITIME_WARNING_DOMAIN_REGISTRY_KEYS: MaritimeWarningDomainRegistryKey[] = [
  "coastal",
  "northSea",
];

export function maritimeWarningDomainEntries(): {
  registryKey: MaritimeWarningDomainRegistryKey;
  domain: (typeof MARITIME_WARNING_DOMAINS)[MaritimeWarningDomainRegistryKey];
}[] {
  return MARITIME_WARNING_DOMAIN_REGISTRY_KEYS.map((registryKey) => ({
    registryKey,
    domain: MARITIME_WARNING_DOMAINS[registryKey],
  }));
}

export function domainIdFromApiAreaCategory(
  areaCategory: string,
): MaritimeWarningDomainId | undefined {
  const entry = maritimeWarningDomainEntries().find(
    ({ domain }) => domain.api.areaCategory === areaCategory,
  );
  return entry?.domain.id;
}

export function apiAreaCategoryFromDomainId(domainId: MaritimeWarningDomainId): string {
  const entry = maritimeWarningDomainEntries().find(({ domain }) => domain.id === domainId);
  if (!entry) {
    throw new Error(`Unknown maritime domain id: ${domainId}`);
  }
  return entry.domain.api.areaCategory;
}

/**
 * `enabled_domains` (e.g. `["kust", "noordzee"]`) to internal ids, in registry order.
 * Unknown values, duplicates and non-array payloads are ignored.
 */
export function domainIdsFromEnabledDomains(
  enabledDomains: readonly string[] | null | undefined,
): MaritimeWarningDomainId[] {
  const values = new Set<string>(Array.isArray(enabledDomains) ? enabledDomains : []);
  return maritimeWarningDomainEntries()
    .filter(({ domain }) => values.has(domain.api.enabledDomain))
    .map(({ domain }) => domain.id);
}

/** Internal ids to the `enabled_domains` array, in registry order. */
export function enabledDomainsFromDomainIds(
  domainIds: MaritimeWarningDomainId[],
): MaritimeWarningSettingsEnabledDomainsEnum[] {
  return maritimeWarningDomainEntries()
    .filter(({ domain }) => domainIds.includes(domain.id))
    .map(({ domain }) => domain.api.enabledDomain);
}

export function isMaritimeWarningDomainId(value: string): value is MaritimeWarningDomainId {
  return maritimeWarningDomainEntries().some(({ domain }) => domain.id === value);
}

export function isCoastalMaritimeDomainId(domainId: string): boolean {
  return domainId === MARITIME_WARNING_DOMAINS.coastal.id;
}

export function defaultDomainId(
  enabledDomainIds: MaritimeWarningDomainId[],
): MaritimeWarningDomainId {
  const coastal = MARITIME_WARNING_DOMAINS.coastal.id;
  if (enabledDomainIds.includes(coastal)) {
    return coastal;
  }
  return enabledDomainIds[0] ?? coastal;
}

export function maritimeDomainsForIds(enabledDomainIds: MaritimeWarningDomainId[]) {
  return maritimeWarningDomainEntries().filter(({ domain }) =>
    enabledDomainIds.includes(domain.id),
  );
}
